<script setup>
// Right Chat - criar / editar / excluir etapa do board.
//
// `description` (<=120) e `cancelled` sao os dois campos que ensinam a IA QUANDO mover o
// card e o que e balde de perdido. Sem tela para eles, o funil roda burro - por isso
// esta janela entra no minimo, e nao numa fase 2.
import { ref, computed } from 'vue';
import RightKanbanModal from './RightKanbanModal.vue';
import { useRightKanban } from '../../../composables/useRightKanban';

const props = defineProps({
  boardId: { type: Number, required: true },
  step: { type: Object, default: null },
  nextPosition: { type: Number, default: 0 },
});

const emit = defineEmits(['close']);

const { createStep, updateStep, deleteStep, saving, error } = useRightKanban();

const isEdit = computed(() => !!props.step);
const form = ref({
  name: props.step?.name || '',
  description: props.step?.description || '',
  color: props.step?.color || '#4F46E5',
  cancelled: !!props.step?.cancelled,
  position: Number.isFinite(props.step?.position)
    ? props.step.position
    : props.nextPosition,
});
const confirmingDelete = ref(false);

const descriptionLeft = computed(() => 120 - (form.value.description || '').length);
const canSubmit = computed(
  () => form.value.name.trim().length > 0 && !saving.value && descriptionLeft.value >= 0
);

const payload = computed(() => ({
  name: form.value.name.trim(),
  description: form.value.description.trim(),
  color: form.value.color,
  cancelled: form.value.cancelled,
  position: Number(form.value.position) || 0,
}));

const submit = async () => {
  if (!canSubmit.value) return;
  const result = isEdit.value
    ? await updateStep(props.boardId, props.step.id, payload.value)
    : await createStep(props.boardId, payload.value);
  if (result) emit('close');
};

const remove = async () => {
  if (!confirmingDelete.value) {
    confirmingDelete.value = true;
    return;
  }
  // Etapa com card volta 409 com a mensagem do servidor; o banner de erro mostra e a
  // janela fica aberta de proposito, para o operador mover os cards antes.
  const result = await deleteStep(props.boardId, props.step.id);
  if (result) emit('close');
};
</script>

<template>
  <RightKanbanModal
    :title="isEdit ? 'Editar etapa' : 'Nova etapa'"
    @close="emit('close')"
  >
    <label class="rk-field">
      <span class="rk-field__label">Nome</span>
      <input
        v-model="form.name"
        class="rk-field__input"
        type="text"
        maxlength="255"
        placeholder="Qualificado"
      />
      <span class="rk-field__hint">
        Nome duplicado no mesmo board quebra o "mover por nome" da IA.
      </span>
    </label>

    <label class="rk-field">
      <span class="rk-field__label">Descricao (o que ensina a IA)</span>
      <textarea
        v-model="form.description"
        class="rk-field__input rk-field__input--area"
        maxlength="120"
        rows="2"
        placeholder="Lead respondeu e tem interesse confirmado"
      />
      <span class="rk-field__hint">{{ descriptionLeft }} caracteres restantes</span>
    </label>

    <div class="rk-row">
      <label class="rk-field rk-field--inline">
        <span class="rk-field__label">Cor</span>
        <input v-model="form.color" class="rk-field__color" type="color" />
      </label>

      <label class="rk-field rk-field--inline">
        <span class="rk-field__label">Posicao</span>
        <input
          v-model.number="form.position"
          class="rk-field__input rk-field__input--small"
          type="number"
          min="0"
        />
      </label>
    </div>

    <label class="rk-check">
      <input v-model="form.cancelled" type="checkbox" />
      <span>
        Etapa de perdido — sem isso a IA trata esta coluna como avanco no funil.
      </span>
    </label>

    <p v-if="isEdit && confirmingDelete" class="rk-danger">
      Clique em "Excluir" de novo para confirmar. Etapa com card nao e excluida.
    </p>

    <p v-if="error" class="rk-error">{{ error }}</p>

    <template #footer>
      <button
        v-if="isEdit"
        type="button"
        class="rk-btn rk-btn--danger"
        :disabled="saving"
        @click="remove"
      >
        Excluir
      </button>
      <button type="button" class="rk-btn" @click="emit('close')">Cancelar</button>
      <button
        type="button"
        class="rk-btn rk-btn--primary"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ saving ? 'Salvando...' : 'Salvar' }}
      </button>
    </template>
  </RightKanbanModal>
</template>

<style scoped>
.rk-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rk-field--inline {
  flex: 1;
}

.rk-row {
  display: flex;
  gap: 12px;
}

.rk-field__label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.rk-field__hint {
  font-size: 11px;
  color: #94a3b8;
}

.rk-field__input {
  padding: 8px 10px;
  font-size: 13px;
  color: #0f172a;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.rk-field__input--area {
  resize: vertical;
}

.rk-field__input--small {
  width: 100%;
}

.rk-field__input:focus {
  border-color: #4f46e5;
  outline: none;
}

.rk-field__color {
  width: 100%;
  height: 34px;
  padding: 2px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.rk-check {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12px;
  color: #475569;
}

.rk-error,
.rk-danger {
  padding: 8px 10px;
  margin: 0;
  font-size: 12px;
  border-radius: 8px;
}

.rk-error {
  color: #b91c1c;
  background: #fee2e2;
}

.rk-danger {
  color: #92400e;
  background: #fef3c7;
}

.rk-btn {
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.rk-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.rk-btn--primary {
  color: #ffffff;
  background: #4f46e5;
  border-color: #4f46e5;
}

.rk-btn--danger {
  margin-right: auto;
  color: #b91c1c;
  border-color: #fecaca;
}

.dark .rk-field__label,
.dark .rk-check {
  color: #cbd5e1;
}

.dark .rk-field__input,
.dark .rk-field__color {
  color: #e2e8f0;
  background: #0f172a;
  border-color: #334155;
}

.dark .rk-btn {
  color: #cbd5e1;
  background: #1e293b;
  border-color: #334155;
}

.dark .rk-btn--primary {
  color: #ffffff;
  background: #4f46e5;
  border-color: #4f46e5;
}
</style>
